import React, { useState, useEffect } from 'react';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getCurrencySettings } from '../../../services/migration/companyDataService';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  MenuItem,
  Select,
  Alert,
  FormControl,
  InputLabel,
  InputAdornment,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../../../contexts/AuthContext';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import PaymentReceiptModal from './PaymentReceiptModal';
import { useTranslation } from 'react-i18next';
import ProjectSelector from '../shared/ProjectSelector';

// Ödeme yöntemleri
const paymentMethods = [
  { id: 'cash', tr: 'Nakit', en: 'Cash' },
  { id: 'bank_transfer', tr: 'Havale/EFT', en: 'Bank Transfer/EFT' },
  { id: 'credit_card', tr: 'Kredi Kartı', en: 'Credit Card' },
  { id: 'accrual', tr: 'Tahakkuk', en: 'Accrual' },
];

interface CreditCardItem {
  id: number;
  accountName: string;
  cardNumber: string;
  creditLimit: number;
  usedAmount: number;
  availableLimit: number;
  currency: string;
}

interface Currency {
  id: string;
  name: string;
  symbol: string;
  code: string;
  isDefault?: boolean;
}

interface Cashbox {
  id: string;
  name: string;
  description?: string;
  currency: string;
  balance: number;
  createdAt?: string;
  isActive?: boolean;
  branchId?: string | null;
  isDefault?: boolean;
  hasTransactions?: boolean;
  currencyCode?: string;
  currencySymbol?: string;
}

interface BankAccount {
  id: string;
  bankName: string;
  accountName?: string;
  branchName?: string;
  accountNumber: string;
  iban?: string;
  currency: string;
  balance: number;
  isActive?: boolean;
  isPosAccount?: boolean; 
  isCheckAccount?: boolean;
  currencySymbol?: string;
  parentBankAccountId?: string;
  isSubAccount?: boolean;
}

interface MakePaymentModalProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  accountName: string;
  companyId: string | null;
  onSuccess?: () => void;
  /** İşlemi yapan ekranın şubesi (manager panelinde kilitli şube) */
  branchId?: string | null;
  /** true ise şube seçimi kilitlidir (manager paneli) */
  branchLocked?: boolean;
}

const MakePaymentModal: React.FC<MakePaymentModalProps> = ({
  open,
  onClose,
  accountId,
  accountName,
  companyId,
  onSuccess,
  branchId = null,
  branchLocked = false
}) => {
  const { userDetails, branches } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [insufficientFunds, setInsufficientFunds] = useState(false);
  const [insufficientFundsMessage, setInsufficientFundsMessage] = useState('');

  // Form states
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [cashboxes, setCashboxes] = useState<Cashbox[]>([]);
  const [filteredCashboxes, setFilteredCashboxes] = useState<Cashbox[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [filteredBankAccounts, setFilteredBankAccounts] = useState<BankAccount[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [selectedCashbox, setSelectedCashbox] = useState<Cashbox | null>(null);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
  const [creditCards, setCreditCards] = useState<CreditCardItem[]>([]);
  const [filteredCreditCards, setFilteredCreditCards] = useState<CreditCardItem[]>([]);
  const [selectedCreditCard, setSelectedCreditCard] = useState<CreditCardItem | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState('');
  // PYS: hareketi bir projeye bağlar (opsiyonel)
  const [projectId, setProjectId] = useState<number | null>(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [documentNo, setDocumentNo] = useState<string>(''); // Belge numarası
  const [accountBalance, setAccountBalance] = useState<number>(0);
  // Şube seçimi ('' = Genel; manager panelinde kilitli)
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  // ✅ İşlem tarihi (geri/ileri tarihli işlem desteği) — YYYY-MM-DD formatında string
  const [transactionDate, setTransactionDate] = useState<string>(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  });
  
  // Tediye fişi modalı için state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [completedTransaction, setCompletedTransaction] = useState<any>(null);

  useEffect(() => {
    if (open && companyId && accountId) {
      loadAccountDetails();
    }
  }, [open, companyId, accountId]);

  // Otomatik belge numarası oluştur
  const generateDocumentNo = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    // Format: TED-YYYYMMDD-HHMMSS
    return `TED-${year}${month}${day}-${hours}${minutes}${seconds}`;
  };

  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(false);
      setSuccessMessage('');
      setSelectedCurrency(null);
      setSelectedPaymentMethod('cash');
      setSelectedCashbox(null);
      setSelectedBankAccount(null);
      setSelectedCreditCard(null);
      setAmount('');
      setDescription('');
      setProjectId(null);
      setInfoDialogOpen(false);
      setSelectedBranchId(branchId || ''); // Şube: prop'tan gelen değerle başlat
      setDocumentNo(generateDocumentNo()); // Otomatik belge numarası oluştur
      // ✅ İşlem tarihini bugüne sıfırla
      const t = new Date();
      setTransactionDate(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`);
      loadCurrencies();
    }
  }, [open, companyId]);

  useEffect(() => {
    if (selectedCurrency) {
      loadCashboxes();
      loadBankAccounts();
      loadCreditCards();
    }
  }, [selectedCurrency, companyId]);

  useEffect(() => {
    if (selectedCurrency && creditCards.length > 0) {
      const filtered = creditCards.filter(card => card.currency === selectedCurrency.code);
      setFilteredCreditCards(filtered);
    } else {
      setFilteredCreditCards([]);
    }
  }, [selectedCurrency, creditCards]);

  useEffect(() => {
    if (selectedCurrency && cashboxes.length > 0) {
      const filtered = cashboxes.filter(cashbox => cashbox.currency === selectedCurrency.code);
      setFilteredCashboxes(filtered);
    }
  }, [selectedCurrency, cashboxes]);

  useEffect(() => {
    if (selectedCurrency && bankAccounts.length > 0) {
      const filtered = bankAccounts.filter(account => account.currency === selectedCurrency.code);
      setFilteredBankAccounts(filtered);
    }
  }, [selectedCurrency, bankAccounts]);

  useEffect(() => {
    if (selectedPaymentMethod !== 'cash') {
      setSelectedCashbox(null);
    }
    
    if (selectedPaymentMethod === 'cash') {
      setSelectedBankAccount(null);
    }

    if (selectedPaymentMethod !== 'credit_card') {
      setSelectedCreditCard(null);
    }
  }, [selectedPaymentMethod]);

  const loadCurrencies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currencyData = await getCurrencySettings(companyId!);
      
      const settings: {[code: string]: boolean} = { TRY: true };
      
      if (currencyData) {
        Object.entries(currencyData).forEach(([code, active]: [string, any]) => {
          settings[code] = Boolean(active);
        });
      }
      
      const availableCurrencies: string[] = [
        'TRY', 'USD', 'EUR', 'GBP', 'CHF', 'JPY', 'AUD', 'CAD', 'RUB', 'AED', 'SAR', 'NOK', 'DKK', 'SEK'
      ];
      
      const activeCurrencyCodes = availableCurrencies.filter(code => settings[code]);
      
      const currenciesData: Currency[] = activeCurrencyCodes.map(code => ({
        id: code,
        name: getCurrencyName(code),
        symbol: getCurrencySymbol(code),
        code: code,
        isDefault: code === 'TRY'
      }));
      
      setCurrencies(currenciesData);
      
      const defaultCurrency = currenciesData.find(c => c.isDefault === true);
      if (defaultCurrency) {
        setSelectedCurrency(defaultCurrency);
      } else if (currenciesData.length > 0) {
        setSelectedCurrency(currenciesData[0]);
      }
      
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError(tt('Para birimleri yüklenirken hata oluştu: ', 'An error occurred while loading currencies: ') + err.message);
    }
  };

  const getCurrencySymbol = (code: string): string => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return code;
    }
  };

  const getCurrencyName = (code: string): string => {
    switch(code) {
      case 'TRY': return tt('Türk Lirası', 'Turkish Lira');
      case 'USD': return tt('Amerikan Doları', 'US Dollar');
      case 'EUR': return tt('Euro', 'Euro');
      case 'GBP': return tt('İngiliz Sterlini', 'British Pound');
      case 'JPY': return tt('Japon Yeni', 'Japanese Yen');
      case 'CHF': return tt('İsviçre Frangı', 'Swiss Franc');
      case 'AUD': return tt('Avustralya Doları', 'Australian Dollar');
      case 'CAD': return tt('Kanada Doları', 'Canadian Dollar');
      case 'CNY': return tt('Çin Yuanı', 'Chinese Yuan');
      default: return code;
    }
  };

  const loadCashboxes = async () => {
    try {
      setLoading(true);
      
      const response = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);

      const result = response.data;
      const sqlCashboxes = result.data || result;
      
      if (!sqlCashboxes || sqlCashboxes.length === 0) {
        setCashboxes([]);
        setFilteredCashboxes([]);
        setLoading(false);
        return;
      }
      
      const cashboxList: Cashbox[] = sqlCashboxes
        .filter((data: any) => data.isActive !== false)
        .map((data: any) => {
          const currencyCode = data.currency || 'TRY';
          
          return {
            id: `cashbox_${data.id}`,
            name: data.name || '',
            description: data.description || '',
            currency: currencyCode,
            currencyCode: currencyCode,
            currencySymbol: getCurrencySymbol(currencyCode),
            balance: data.balance || 0,
            createdAt: data.createdAt || '',
            isActive: data.isActive !== false,
            branchId: data.branchId || null,
            isDefault: data.isDefault || false,
            hasTransactions: data.hasTransactions || false
          };
        });
      
      cashboxList.sort((a, b) => {
        if (a.name === "Genel Kasa") return -1;
        if (b.name === "Genel Kasa") return 1;
        return a.name.localeCompare(b.name);
      });
      
      setCashboxes(cashboxList);
      
      if (selectedCurrency) {
        const filtered = cashboxList.filter(cashbox => cashbox.currency === selectedCurrency.code);
        setFilteredCashboxes(filtered);
      }
      
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError(tt('Kasalar yüklenirken hata oluştu: ', 'An error occurred while loading cashboxes: ') + err.message);
    }
  };

  const loadBankAccounts = async () => {
    try {
      setLoading(true);
      
      const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY);

      const result = response.data;
      const sqlBankAccounts = result.data || result;
      
      if (!sqlBankAccounts || sqlBankAccounts.length === 0) {
        setBankAccounts([]);
        setFilteredBankAccounts([]);
        setLoading(false);
        return;
      }
      
      const accountsList: BankAccount[] = [];
      
      sqlBankAccounts.forEach((data: any) => {
        const currencyCode = data.currency || 'TRY';
        
        accountsList.push({
          id: `bank_${data.id}`,
          bankName: data.bankName || '',
          branchName: data.branchName || '',
          accountNumber: data.accountNumber || '',
          iban: data.iban || '',
          currency: currencyCode,
          currencySymbol: getCurrencySymbol(currencyCode),
          balance: data.balance || 0,
          isActive: data.isActive !== false,
          isPosAccount: data.isPosAccount || false,
          isCheckAccount: data.isCheckAccount || false,
          isSubAccount: false,
          parentBankAccountId: undefined
        });
        
        if (data.subAccounts && data.subAccounts.length > 0) {
          data.subAccounts.forEach((subAccount: any) => {
            const subCurrencyCode = subAccount.currency || 'TRY';
            
            accountsList.push({
              id: `sub_${subAccount.id}`,
              bankName: `${data.bankName} - ${subAccount.accountName || 'Alt Hesap'}`,
              accountName: subAccount.accountName || 'Alt Hesap',
              branchName: data.branchName || '',
              accountNumber: subAccount.accountNumber || '',
              iban: subAccount.iban || '',
              currency: subCurrencyCode,
              currencySymbol: getCurrencySymbol(subCurrencyCode),
              balance: subAccount.balance || 0,
              isActive: subAccount.isActive !== false,
              isPosAccount: subAccount.isPosAccount || false,
              isCheckAccount: subAccount.isCheckAccount || false,
              isSubAccount: true,
              parentBankAccountId: data.id.toString()
            });
          });
        }
      });
      
      accountsList.sort((a, b) => a.bankName.localeCompare(b.bankName));
      setBankAccounts(accountsList);
      
      if (selectedCurrency) {
        const filtered = accountsList.filter(account => account.currency === selectedCurrency.code);
        setFilteredBankAccounts(filtered);
      }
      
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError(tt('Banka hesapları yüklenirken hata oluştu: ', 'An error occurred while loading bank accounts: ') + err.message);
    }
  };

  const loadCreditCards = async () => {
    try {
      const response = await accountingApi.get(ACCOUNTING_API.CREDIT_CARDS);
      const result = response.data;
      const rawCards = result.data || result || [];

      const cards: CreditCardItem[] = rawCards
        .filter((c: any) => c.isActive !== false && !c.isDeleted)
        .map((c: any) => {
          const creditLimit = Number(c.creditLimit) || 0;
          const usedAmount = Number(c.usedAmount) || 0;
          return {
            id: c.id,
            accountName: c.accountName || '',
            cardNumber: c.cardNumber || '',
            creditLimit,
            usedAmount,
            availableLimit: creditLimit - usedAmount,
            currency: c.currency || 'TRY',
          } as CreditCardItem;
        });

      setCreditCards(cards);

      if (selectedCurrency) {
        setFilteredCreditCards(cards.filter(card => card.currency === selectedCurrency.code));
      }
    } catch (err: any) {
      console.error('[MakePaymentModal] Kredi kartları yüklenemedi:', err);
      setCreditCards([]);
      setFilteredCreditCards([]);
    }
  };

  const loadAccountDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[MakePaymentModal] 🔍 loadAccountDetails başladı:', { companyId, accountId });
      
      // ✅ MULTI-TENANT: companyId parametresi ekle
      if (!companyId) {
        throw new Error('CompanyId bulunamadı');
      }
      
      // 🔥 YENİ: SQL API'den direkt cari hesap bilgisini çek
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_ID(parseInt(accountId)));

      const result = response.data;
      const accountData = result.data || result;
      
      console.log('[MakePaymentModal] 📦 SQL API sonucu:', accountData);
      
      if (accountData) {
        const creditBalance = accountData.creditBalance !== undefined ? accountData.creditBalance : (accountData.balance || 0);
        const debitBalance = accountData.debitBalance || 0;
        const calculatedBalance = creditBalance - debitBalance;
        
        setAccountBalance(calculatedBalance);
      } else {
        setAccountBalance(0);
        setError(tt('Cari hesap bilgileri bulunamadı.', 'Current account details not found.'));
      }
      
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError(tt('Cari hesap bilgileri yüklenirken hata oluştu: ', 'An error occurred while loading current account details: ') + err.message);
    }
  };

  const formatCurrency = (amount: number, currencyCode: string = 'TRY') => {
    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Türk formatında sayı formatlama (1.000,00) — DepositModal/WithdrawModal ile aynı pattern
  const formatTurkishNumber = (value: string): string => {
    if (!value) return '';
    const cleanValue = value.replace(/[^0-9,]/g, '');
    const parts = cleanValue.split(',');
    const integerPart = parts[0];
    const decimalPart = parts[1] || '';
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const limitedDecimal = decimalPart.slice(0, 2);
    if (cleanValue.includes(',')) {
      return `${formattedInteger},${limitedDecimal}`;
    }
    return formattedInteger;
  };

  // Formatı temizle ve sayıya çevir (1.000,50 → 1000.5)
  const parseFormattedNumber = (value: string): number => {
    if (!value) return 0;
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Sayısal girdi kontrolü ve formatlama (input onChange handler)
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value && !/^[0-9.,]*$/.test(value)) return;
    if ((value.match(/,/g) || []).length > 1) return;
    if (value.includes(',')) {
      const parts = value.split(',');
      if (parts[1] && parts[1].length > 2) return;
    }
    setAmount(formatTurkishNumber(value));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!companyId || !accountId || !selectedCurrency) {
        setError(tt('Gerekli bilgiler eksik.', 'Required information is missing.'));
        setLoading(false);
        return;
      }
      
      const amountValue = parseFormattedNumber(amount);
      if (isNaN(amountValue) || amountValue <= 0) {
        setError(tt('Geçerli bir miktar giriniz.', 'Please enter a valid amount.'));
        setLoading(false);
        return;
      }
      
      // Belge numarası kontrolü
      if (!documentNo || documentNo.trim() === '') {
        setError(tt('Belge numarası zorunludur. Lütfen belge numarası giriniz.', 'Document number is required. Please enter a document number.'));
        setLoading(false);
        return;
      }

      // ✅ İşlem tarihi parse + validasyon + 30 günden uzak için yumuşak uyarı
      const txDateParts = transactionDate.split('-');
      if (txDateParts.length !== 3) {
        setError(tt('Geçerli bir işlem tarihi seçiniz.', 'Please select a valid transaction date.'));
        setLoading(false);
        return;
      }
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const selected = new Date(Number(txDateParts[0]), Number(txDateParts[1]) - 1, Number(txDateParts[2]));
      if (isNaN(selected.getTime())) {
        setError(tt('Geçerli bir işlem tarihi seçiniz.', 'Please select a valid transaction date.'));
        setLoading(false);
        return;
      }
      const diffDays = Math.round((selected.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (Math.abs(diffDays) > 30) {
        const human = selected.toLocaleDateString(localeCode);
        const isBackdated = diffDays < 0;
        const msg = isBackdated
          ? tt(`Seçtiğiniz işlem tarihi (${human}) bugünden ${Math.abs(diffDays)} gün öncedir. Devam etmek istediğinize emin misiniz?`, `The selected transaction date (${human}) is ${Math.abs(diffDays)} days before today. Are you sure you want to continue?`)
          : tt(`Seçtiğiniz işlem tarihi (${human}) bugünden ${diffDays} gün sonradır. Devam etmek istediğinize emin misiniz?`, `The selected transaction date (${human}) is ${diffDays} days after today. Are you sure you want to continue?`);
        const ok = window.confirm(msg);
        if (!ok) {
          setLoading(false);
          return;
        }
      }
      // Bugün seçildiyse şimdiki saat, geçmiş/gelecek seçildiyse o günün öğleni (timezone'a güvenli)
      const finalDate = (diffDays === 0)
        ? new Date()
        : new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 12, 0, 0);

      // ✅ Tarihi Türkiye yerel saatiyle (timezone kaymadan) gönder.
      // toISOString() UTC'ye çevirip 3 saat geriye kaydırıyordu; backend UTC+3 yerel saat
      // konvansiyonu kullandığı için hareketler 3 saat geride kaydolup listede aşağı düşüyordu.
      const pad2 = (n: number) => String(n).padStart(2, '0');
      const finalDateIso = `${finalDate.getFullYear()}-${pad2(finalDate.getMonth() + 1)}-${pad2(finalDate.getDate())}T${pad2(finalDate.getHours())}:${pad2(finalDate.getMinutes())}:${pad2(finalDate.getSeconds())}`;

      if (selectedPaymentMethod === 'accrual') {
        // Tahakkuk için kasa/banka kontrolü gerekmez
      } else if (selectedPaymentMethod === 'credit_card') {
        if (!selectedCreditCard) {
          setError(tt('Lütfen bir kredi kartı seçiniz.', 'Please select a credit card.'));
          setLoading(false);
          return;
        }
        if (amountValue > selectedCreditCard.availableLimit) {
          setError(tt(
            `Kart limiti yetersiz. Kalan limit: ${selectedCreditCard.availableLimit.toLocaleString(localeCode)} ${selectedCreditCard.currency}`,
            `Insufficient card limit. Available limit: ${selectedCreditCard.availableLimit.toLocaleString(localeCode)} ${selectedCreditCard.currency}`
          ));
          setLoading(false);
          return;
        }
      } else if (selectedPaymentMethod === 'cash') {
        if (!selectedCashbox) {
          setError(tt('Lütfen bir kasa seçiniz.', 'Please select a cashbox.'));
          setLoading(false);
          return;
        }
        
        // ✅ YENİ: Frontend bakiye kontrolü KALDIRILDI!
        // Bakiyeler artık işlem kayıtlarından hesaplandığı için,
        // frontend'deki selectedCashbox.balance güncel olmayabilir.
        // Backend zaten doğru bakiye kontrolünü yapacak.
        
      } else {
        if (!selectedBankAccount) {
          setError(tt('Lütfen bir banka hesabı seçiniz.', 'Please select a bank account.'));
          setLoading(false);
          return;
        }
        
        // ✅ YENİ: Frontend bakiye kontrolü KALDIRILDI!
        // Bakiyeler artık işlem kayıtlarından hesaplandığı için,
        // frontend'deki selectedBankAccount.balance güncel olmayabilir.
        // Backend zaten doğru bakiye kontrolünü yapacak.
        
      }
      
      let cashboxId = null;
      if (selectedPaymentMethod === 'cash' && selectedCashbox) {
        cashboxId = selectedCashbox.id.replace('cashbox_', '');
      }
      
      let bankAccountId = null;
      let bankSubAccountId = null;
      
      if (selectedPaymentMethod !== 'cash' && selectedPaymentMethod !== 'accrual' && selectedBankAccount) {
        if (selectedBankAccount.isSubAccount) {
          bankAccountId = selectedBankAccount.parentBankAccountId;
          bankSubAccountId = selectedBankAccount.id.replace('sub_', '');
        } else {
          bankAccountId = selectedBankAccount.id.replace('bank_', '');
          bankSubAccountId = null;
        }
      }
      
      const paymentPayload = {
        companyId: companyId,
        accountId: accountId,
        accountName: accountName,
        amount: amountValue,
        currency: selectedCurrency.code,
        paymentMethod: selectedPaymentMethod,
        cashboxId: cashboxId,
        cashboxName: selectedCashbox?.name || null,
        bankAccountId: bankAccountId,
        bankSubAccountId: bankSubAccountId,
        bankAccountName: selectedBankAccount?.bankName || null,
        creditCardId: selectedPaymentMethod === 'credit_card' ? selectedCreditCard?.id ?? null : null,
        description: description || tt(`${accountName} carisine ${selectedPaymentMethod === 'accrual' ? 'tahakkuk ' : ''}ödeme`, `${selectedPaymentMethod === 'accrual' ? 'Accrual ' : ''}payment to ${accountName}`),
        documentNo: documentNo.trim(), // Belge numarasını ekle
        date: finalDateIso, // ✅ Türkiye yerel saati (geri/ileri tarihli işlem desteği)
        createdBy: userDetails?.email || tt('Sistem', 'System'),
        // Şube: gönderilmezse backend cari hesabın şubesine düşer
        branchId: selectedBranchId || null,
        // PYS: proje bağı; backend kasa/banka yansımasına da taşır
        projectId: projectId,
      };

      const response = await accountingApi.post(ACCOUNTING_API.TRANSACTION_PAYMENT, paymentPayload);

      const result = response.data;
      console.log('Ödeme işlemi başarılı:', result);
      
      // ✅ İşlem başarılı - Tediye fişi için transaction objesini oluştur
      const transactionData = {
        id: result.transactionId || result.id || 'temp',
        accountId: accountId,
        accountName: accountName,
        transactionType: 'PAYMENT',
        type: 'PAYMENT',
        amount: amountValue,
        debitAmount: amountValue,
        creditAmount: 0,
        description: description || tt(`${accountName} carisine ${selectedPaymentMethod === 'accrual' ? 'tahakkuk ' : ''}ödeme`, `${selectedPaymentMethod === 'accrual' ? 'Accrual ' : ''}payment to ${accountName}`),
        documentNo: documentNo.trim(),
        date: finalDateIso, // ✅ Kullanıcı seçtiği işlem tarihi (Türkiye yerel saati)
        createdAt: new Date().toISOString(),
        isIncome: false,
        currency: selectedCurrency.code,
        paymentMethod: selectedPaymentMethod,
        cashboxId: cashboxId,
        cashboxName: selectedCashbox?.name || null,
        bankAccountId: bankAccountId,
        bankAccountName: selectedBankAccount?.bankName || null,
      };
      
      setCompletedTransaction(transactionData);
      setSuccess(true);
      setSuccessMessage(tt('Ödeme başarıyla kaydedildi!', 'Payment has been saved successfully!'));
        
      // ✅ 1.5 saniye sonra success ekranını kapat ve tediye fişini göster
      setTimeout(() => {
        setSuccess(false);
        setShowReceiptModal(true);
      }, 1500);
        
      setLoading(false);
    } catch (err: any) {
      console.error('Ödeme kaydedilemedi:', err);
      setLoading(false);
      setError(tt('Ödeme kaydedilemedi: ', 'Payment could not be saved: ') + err.message);
    }
  };

  const handleCloseInsufficientFundsModal = () => {
    setInsufficientFunds(false);
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
  };

  const handleCloseModal = () => {
    setInfoDialogOpen(false);
    onClose();
  };
  
  // Tediye fişi modalını kapat ve ana modalı kapat
  const handleCloseReceiptModal = () => {
    setShowReceiptModal(false);
    setCompletedTransaction(null);
    if (onSuccess) onSuccess();
    onClose();
  };

  // Kasa/Banka alanı görünür mü?
  const showCashboxBankField = selectedPaymentMethod === 'cash' || selectedPaymentMethod === 'bank_transfer';

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{tt('Cariye Ödeme Yap', 'Make Payment to Current Account')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton edge="end" color="inherit" onClick={() => setInfoDialogOpen(true)} disabled={loading} aria-label={tt('Bilgilendirme', 'Information')}>
                <InfoOutlinedIcon />
              </IconButton>
              <IconButton edge="end" color="inherit" onClick={handleCloseModal} disabled={loading}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {success ? (
            <Box textAlign="center" p={3}>
              <CheckCircleOutlineIcon color="success" style={{ fontSize: 60 }} />
              <Typography variant="h6" color="success" gutterBottom sx={{ mt: 2 }}>
                {successMessage}
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Cari Hesap Bilgisi */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {tt('Cari Hesap', 'Current Account')}
                </Typography>
                <Typography variant="h6" gutterBottom>{accountName}</Typography>
                <Typography 
                  variant="body1" 
                  color={accountBalance >= 0 ? "success.main" : "error.main"} 
                  fontWeight="bold"
                >
                  {tt('Bakiye', 'Balance')}: {formatCurrency(accountBalance)}
                </Typography>
              </Paper>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Para Birimi */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="currency-select-label">{tt('Para Birimi *', 'Currency *')}</InputLabel>
                <Select
                  labelId="currency-select-label"
                  value={selectedCurrency?.code || ''}
                  label={tt('Para Birimi *', 'Currency *')}
                  onChange={(e) => {
                    const currency = currencies.find(c => c.code === e.target.value);
                    setSelectedCurrency(currency || null);
                  }}
                  disabled={loading}
                >
                  {currencies.map(currency => (
                    <MenuItem key={currency.id} value={currency.code}>
                      {currency.name} ({currency.symbol})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Ödeme Yöntemi */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="payment-method-select-label">{tt('Ödeme Yöntemi *', 'Payment Method *')}</InputLabel>
                <Select
                  labelId="payment-method-select-label"
                  value={selectedPaymentMethod}
                  label={tt('Ödeme Yöntemi *', 'Payment Method *')}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  disabled={loading}
                >
                  {paymentMethods.map(method => (
                    <MenuItem key={method.id} value={method.id}>
                      {isTr ? method.tr : method.en}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Şube Seçimi */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="branch-select-label">{tt('Şube', 'Branch')}</InputLabel>
                <Select
                  labelId="branch-select-label"
                  value={selectedBranchId}
                  label={tt('Şube', 'Branch')}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  disabled={loading || branchLocked}
                >
                  <MenuItem value="">
                    <em>{tt('Genel (cari şubesi)', 'General (account branch)')}</em>
                  </MenuItem>
                  {branches.map(branch => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Kasa/Banka Seçimi (Dinamik) */}
              {showCashboxBankField && (
                <>
                  {selectedPaymentMethod === 'cash' ? (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel id="cashbox-select-label">{tt('Kasa Seçin *', 'Select Cashbox *')}</InputLabel>
                      <Select
                        labelId="cashbox-select-label"
                        value={selectedCashbox?.id || ''}
                        label={tt('Kasa Seçin *', 'Select Cashbox *')}
                        onChange={(e) => {
                          const cashbox = filteredCashboxes.find(c => c.id === e.target.value);
                          setSelectedCashbox(cashbox || null);
                        }}
                        disabled={loading || filteredCashboxes.length === 0}
                      >
                        {filteredCashboxes.length === 0 ? (
                          <MenuItem disabled value="">
                            {tt('Uygun kasa bulunamadı', 'No suitable cashbox found')}
                          </MenuItem>
                        ) : (
                          filteredCashboxes.map(cashbox => (
                            <MenuItem key={cashbox.id} value={cashbox.id}>
                              {cashbox.name} ({tt('Bakiye', 'Balance')}: {cashbox.balance.toLocaleString(localeCode)} {cashbox.currencySymbol})
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel id="bank-select-label">{tt('Banka Hesabı Seçin *', 'Select Bank Account *')}</InputLabel>
                      <Select
                        labelId="bank-select-label"
                        value={selectedBankAccount?.id || ''}
                        label={tt('Banka Hesabı Seçin *', 'Select Bank Account *')}
                        onChange={(e) => {
                          const account = filteredBankAccounts.find(a => a.id === e.target.value);
                          setSelectedBankAccount(account || null);
                        }}
                        disabled={loading || filteredBankAccounts.length === 0}
                      >
                        {filteredBankAccounts.length === 0 ? (
                          <MenuItem disabled value="">
                            {tt('Uygun banka hesabı bulunamadı', 'No suitable bank account found')}
                          </MenuItem>
                        ) : (
                          filteredBankAccounts.map(account => (
                            <MenuItem key={account.id} value={account.id}>
                              {account.bankName} ({tt('Bakiye', 'Balance')}: {account.balance.toLocaleString(localeCode)} {account.currencySymbol})
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  )}
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                    ℹ️ {selectedPaymentMethod === 'cash' ? tt('Nakit ödemeler için kasa seçiniz', 'Select a cashbox for cash payments') : tt('Havale/EFT ödemeler için banka hesabı seçiniz', 'Select a bank account for bank transfer payments')}
                  </Typography>
                </>
              )}

              {/* Kredi Kartı Seçimi */}
              {selectedPaymentMethod === 'credit_card' && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="credit-card-select-label">{tt('Kredi Kartı Seçin *', 'Select Credit Card *')}</InputLabel>
                    <Select
                      labelId="credit-card-select-label"
                      value={selectedCreditCard?.id || ''}
                      label={tt('Kredi Kartı Seçin *', 'Select Credit Card *')}
                      onChange={(e) => {
                        const card = filteredCreditCards.find(c => c.id === Number(e.target.value));
                        setSelectedCreditCard(card || null);
                      }}
                      disabled={loading || filteredCreditCards.length === 0}
                    >
                      {filteredCreditCards.length === 0 ? (
                        <MenuItem disabled value="">
                          {tt('Uygun kredi kartı bulunamadı', 'No suitable credit card found')}
                        </MenuItem>
                      ) : (
                        filteredCreditCards.map(card => (
                          <MenuItem key={card.id} value={card.id}>
                            {card.accountName} {card.cardNumber ? `(${card.cardNumber})` : ''} — {tt('Kalan Limit', 'Available Limit')}: {card.availableLimit.toLocaleString(localeCode)} {card.currency}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  {selectedCreditCard && (
                    <Alert severity={selectedCreditCard.availableLimit > 0 ? 'info' : 'warning'} sx={{ mb: 2 }}>
                      {tt('Limit', 'Limit')}: {selectedCreditCard.creditLimit.toLocaleString(localeCode)} {selectedCreditCard.currency} • {tt('Kullanılan', 'Used')}: {selectedCreditCard.usedAmount.toLocaleString(localeCode)} {selectedCreditCard.currency} • {tt('Kalan', 'Available')}: {selectedCreditCard.availableLimit.toLocaleString(localeCode)} {selectedCreditCard.currency}
                    </Alert>
                  )}
                </>
              )}

              {/* Tahakkuk Bilgisi */}
              {selectedPaymentMethod === 'accrual' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {tt('Tahakkuk ödemesi seçildiğinde, kasa veya banka bakiyesi değişmez, sadece cari hesap hareketleri kaydedilir.', 'When accrual payment is selected, cashbox or bank balance does not change; only current account transactions are recorded.')}
                </Alert>
              )}

              {/* İşlem Tarihi (geri/ileri tarihli işlem desteği) */}
              <TextField
                label={tt('İşlem Tarihi *', 'Transaction Date *')}
                type="date"
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                required
                disabled={loading}
                InputLabelProps={{ shrink: true }}
                helperText={tt('İleri/geri tarihli işlem yapabilirsiniz; varsayılan olarak bugündür.', 'You can record back-dated or future transactions; defaults to today.')}
              />

              {/* Belge Numarası */}
              <TextField
                label={tt('Belge Numarası *', 'Document Number *')}
                value={documentNo}
                onChange={(e) => setDocumentNo(e.target.value)}
                fullWidth
                placeholder={tt('Örn: TED-2024-001', 'Ex: TED-2024-001')}
                sx={{ mb: 2 }}
                required
                disabled={loading}
                helperText={tt('Tediye makbuzu için belge numarası giriniz', 'Enter document number for payment receipt')}
              />

              {/* Miktar — Türkçe formatlama (1.000,00) */}
              <TextField
                label={tt('Miktar *', 'Amount *')}
                value={amount}
                onChange={handleAmountChange}
                fullWidth
                type="text"
                placeholder="0,00"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: selectedCurrency && (
                    <InputAdornment position="start">
                      {selectedCurrency.symbol}
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  inputMode: 'decimal',
                  autoComplete: 'off',
                }}
                required
                disabled={loading}
              />

              {/* Açıklama */}
              <TextField
                label={tt('Açıklama (Opsiyonel)', 'Description (Optional)')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={tt('Ödeme ile ilgili açıklama...', 'Description about payment...')}
                disabled={loading}
              />

              {/* Proje bağı (PYS) */}
              <ProjectSelector value={projectId} onChange={setProjectId} disabled={loading} size="medium" />
            </Box>
          )}
        </DialogContent>
        
        {!success && (
          <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Button
              onClick={handleCloseModal}
              disabled={loading}
            >
              {tt('İptal', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading || !selectedCurrency || !amount || !documentNo || !transactionDate || (showCashboxBankField && selectedPaymentMethod === 'cash' && !selectedCashbox) || (showCashboxBankField && selectedPaymentMethod !== 'cash' && !selectedBankAccount)}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? tt('İşleniyor...', 'Processing...') : tt('💰 Ödemeyi Tamamla', '💰 Complete Payment')}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{tt('Cariye Ödeme Yap Bilgilendirmesi', 'Make Payment Guidance')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {tt(
              'Cariye Ödeme Yap ekranı, cari hesaba yapılan ödemenin resmi ve izlenebilir şekilde kaydedilmesi için kullanılır.',
              'The Make Payment screen is used to record outgoing payments to a current account in an official and traceable way.'
            )}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {tt('Nedir, Ne İçin Kullanılır?', 'What Is It Used For?')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {tt(
              'Bu işlem, cari hesaba yapılan ödemenin tutarını, tarihini ve ödeme kanalını (kasa, banka, tahakkuk) kayıt altına alır. Böylece cari hareket geçmişi, bakiye takibi ve muhasebe raporları doğru şekilde oluşur.',
              'This action records the amount, date, and payment channel (cashbox, bank, accrual) of payments made to a current account. This keeps movement history, balance tracking, and accounting reports accurate.'
            )}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {tt('Neden Önemlidir?', 'Why Is It Important?')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {tt('• Cari borç/alacak dengesinin doğru güncellenmesini sağlar.', '• Ensures debit/credit balance of the account is updated correctly.')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {tt('• Ödemenin hangi kaynaktan çıktığını (kasa/banka) net şekilde gösterir.', '• Clearly shows from which source (cashbox/bank) the payment was made.')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {tt('• Belge numarası ve işlem tarihi ile denetim, mutabakat ve raporlama süreçlerini destekler.', '• Supports audit, reconciliation, and reporting with document number and transaction date.')}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {tt('Ödeme Yöntemleri Kısa Açıklama', 'Payment Methods at a Glance')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {tt('• Nakit/Havale-EFT: İlgili kasa veya banka bakiyesi etkilenir.', '• Cash/Transfer-EFT: related cashbox or bank balance is affected.')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {tt('• Tahakkuk: Kasa/banka bakiyesi değişmez; sadece cari hareket kaydı oluşur.', '• Accrual: cashbox/bank balance does not change; only a current account movement is created.')}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {tt('Kullanım Özeti', 'Quick Usage Summary')}
          </Typography>
          <Typography variant="body2">
            {tt(
              'Para birimi, ödeme yöntemi, ilgili hesap, işlem tarihi, belge numarası ve tutarı girerek ödemeyi kaydedersiniz. İşlem tamamlandığında tediye fişi otomatik açılır ve yazdırılabilir.',
              'You save the payment by entering currency, payment method, related account, transaction date, document number, and amount. After completion, the payment voucher opens automatically and can be printed.'
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>
            {tt('Kapat', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Yetersiz Bakiye Modalı */}
      <Dialog 
        open={insufficientFunds} 
        onClose={handleCloseInsufficientFundsModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{tt('Yetersiz Bakiye', 'Insufficient Balance')}</Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseInsufficientFundsModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {insufficientFundsMessage}
          </Alert>
          <Typography>
            {tt('Lütfen farklı bir kasa/hesap seçin veya ödeme miktarını azaltın.', 'Please select a different cashbox/account or reduce the payment amount.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInsufficientFundsModal} color="primary">
            {tt('Tamam', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={successMessage}
      />
      
      {/* Tediye Fişi Modalı */}
      {completedTransaction && (
        <PaymentReceiptModal
          open={showReceiptModal}
          onClose={handleCloseReceiptModal}
          transaction={completedTransaction}
          companyId={companyId}
        />
      )}
    </>
  );
};

export default MakePaymentModal;
